import React from 'react';

interface BusStop {
  name: string;
  time?: string;
  departureLocation?: string;
}

interface BusRoute {
  id: number;
  title: string;
  stops: BusStop[];
}

const BusRouteCard: React.FC<{ route: BusRoute }> = ({ route }) => (
  <div className="bg-white rounded-2xl p-6 border border-sand-200">
    <div className="flex items-center mb-4">
      <div className="bg-olive-100 rounded-full w-10 h-10 flex items-center justify-center mr-3 flex-shrink-0">
        <span className="font-avenir font-bold text-olive-700">{route.id}</span>
      </div>
      <h4 className="font-avenir font-semibold text-base md:text-lg text-gray-800 leading-snug">
        {route.title}
      </h4>
    </div>

    <div className="relative bg-gradient-to-b from-sand-100 to-olive-100 rounded-xl p-6">
      <div className="relative flex">
        <div className="relative w-8 flex-shrink-0">
          <div
            className="absolute top-2 bottom-2 left-2 w-0.5 bg-sand-400"
            style={{ zIndex: 0 }}
          />
          <div
            className="flex flex-col justify-between h-full relative"
            style={{ zIndex: 1, minHeight: `${route.stops.length * 60}px` }}
          >
            {route.stops.map((_, index) => (
              <div
                key={index}
                className="relative"
                style={{
                  height:
                    index === route.stops.length - 1
                      ? 'auto'
                      : `${100 / (route.stops.length - 1)}%`
                }}
              >
                <div
                  className={`w-4 h-4 rounded-full border-2 border-white ${
                    index === 0
                      ? 'bg-olive-600'
                      : index === route.stops.length - 1
                        ? 'bg-forest-600'
                        : 'bg-sand-400'
                  } shadow-md relative z-10`}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className="flex-1 ml-4 flex flex-col justify-between"
          style={{ minHeight: `${route.stops.length * 60}px` }}
        >
          {route.stops.map((stop, index) => (
            <div key={index} className="flex flex-col justify-center">
              <p className="font-avenir text-sm md:text-base text-gray-800 font-semibold leading-tight mb-1">
                {stop.name}
              </p>
              {stop.time && (
                <p className="font-avenir text-sm font-bold text-olive-700">
                  🕐 {stop.time}
                </p>
              )}
              {stop.departureLocation && (
                <p className="font-avenir text-sm font-bold text-olive-700">
                  📍{' '}
                  <a
                    href={stop.departureLocation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-avenir text-xs text-forest-600 hover:text-forest-700 underline mt-1 inline-flex items-center"
                  >
                    Lugar de salida
                  </a>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const BusSchedule: React.FC = () => {
  const outboundRoutes: BusRoute[] = [
    {
      id: 1,
      title: 'Autobús para asistir a la iglesia',
      stops: [
        { name: 'Sevilla - Capitanía (Avenida de la Borbolla)', time: '10:30' },
        { name: 'Morón de la Frontera', time: '11:30' }
      ]
    },
    {
      id: 2,
      title: 'Autobús para ir a la celebración',
      stops: [
        { name: 'Morón de la Frontera', time: '13:45' },
        { name: 'Hacienda Saltillo-Lasso', time: '14:30' }
      ]
    }
  ];

  const returnRoutes: BusRoute[] = [
    {
      id: 1,
      title: 'Primer turno',
      stops: [
        { name: 'Hacienda Saltillo-Lasso', time: '21:00h' },
        { name: 'Sevilla' }
      ]
    },
    {
      id: 2,
      title: 'Segundo turno',
      stops: [
        {
          name: 'Hacienda Saltillo-Lasso',
          time: 'Al finalizar la fiesta (¡Hasta que el cuerpo aguante!)'
        },
        { name: 'Sevilla' }
      ]
    }
  ];

  return (
    <div id="bus-schedule" className="bg-cream-50 rounded-3xl p-6 md:p-8 shadow-lg border border-sand-200">
      <div className="mb-6">
        <h3 className="font-avenir text-2xl font-bold text-terracotta-800 mb-2 flex items-center justify-center">
          <span className="mr-2">🚌</span>
          Autobuses
        </h3>
        <p className="font-avenir text-sl text-gray-600 text-center">
          Habrá servicio de ida y vuelta para cubrir todo el evento:🥂💃.
        </p>
      </div>

      <h4 className="font-avenir font-semibold text-lg text-gray-800 mb-4">
        Autobuses de ida:
      </h4>
      <div className="space-y-6">
        {outboundRoutes.map((route) => (
          <BusRouteCard key={`ida-${route.id}`} route={route} />
        ))}
      </div>

      <div className="mt-8">
        <h4 className="font-avenir font-semibold text-lg text-gray-800 mb-4">
          Autobuses de Vuelta:
        </h4>
        <div className="space-y-6">
          {returnRoutes.map((route) => (
            <BusRouteCard key={`vuelta-${route.id}`} route={route} />
          ))}
        </div>
      </div>

      <div className="mt-6 p-4 bg-olive-50 rounded-xl border border-olive-200">
        <p className="font-avenir text-sm text-forest-700 text-center">
          <strong>📌 Nota</strong>: los horarios son orientativos. Se confirmarán más adelante.
        </p>
      </div>
    </div>
  );
};

export default BusSchedule;
